import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'sama-shared';
import { getTokens, cardStyle, elevatedCardStyle, insetStyle, applyCssVars } from './theme';
import type { ThemeTokens, ThemeMode } from './theme';

export interface UseThemeReturn {
  mode: ThemeMode;
  t: ThemeTokens;
  card: React.CSSProperties;
  elevatedCard: React.CSSProperties;
  inset: React.CSSProperties;
}

export function useTheme(): UseThemeReturn {
  const mode = useSelector((state: RootState) => state.auth.theme) as ThemeMode;
  const t = getTokens(mode);

  useEffect(() => {
    applyCssVars(mode);
  }, [mode]);

  const card = useMemo(() => cardStyle(t), [t]);
  const elevatedCard = useMemo(() => elevatedCardStyle(t), [t]);
  const inset = useMemo(() => insetStyle(t), [t]);

  return { mode, t, card, elevatedCard, inset };
}

// ─── Responsive Hook ────────────────────────────────────────
export function useResponsive() {
  const [state, setState] = useState(() => ({
    isMobile: typeof window !== 'undefined' && window.innerWidth < 768,
    isTablet: typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: typeof window === 'undefined' || window.innerWidth >= 1024,
  }));

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setState({ isMobile: w < 768, isTablet: w >= 768 && w < 1024, isDesktop: w >= 1024 });
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return state;
}

// ─── Typography Styles ──────────────────────────────────────
export function headingStyle(t: ThemeTokens, level: 1 | 2 | 3 | 4): React.CSSProperties {
  const sizeMap = { 1: t.fontH1, 2: t.fontH2, 3: t.fontH3, 4: t.fontH4 };
  const trackingMap = { 1: '-0.02em', 2: '-0.02em', 3: '-0.01em', 4: '-0.01em' };
  return {
    fontSize: sizeMap[level],
    fontWeight: 600,
    color: t.textPrimary,
    letterSpacing: trackingMap[level],
    lineHeight: 1.3,
    margin: 0,
  };
}

export function captionStyle(t: ThemeTokens): React.CSSProperties {
  return {
    fontSize: t.fontCaption,
    fontWeight: 500,
    color: t.textMuted,
    letterSpacing: '0.02em',
    textTransform: 'uppercase' as const,
    lineHeight: 1.4,
  };
}

export function labelStyle(t: ThemeTokens): React.CSSProperties {
  return {
    fontSize: t.fontLabel,
    fontWeight: 500,
    color: t.textSecondary,
    lineHeight: 1.4,
  };
}

export function bodyStyle(t: ThemeTokens): React.CSSProperties {
  return {
    fontSize: t.fontBody,
    fontWeight: 400,
    color: t.textPrimary,
    lineHeight: 1.5,
  };
}

export function metricStyle(t: ThemeTokens): React.CSSProperties {
  return {
    fontSize: t.fontMetric,
    fontWeight: 600,
    color: t.textPrimary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
  };
}

// ─── Reusable Style Helpers ─────────────────────────────────
export function searchInputStyle(t: ThemeTokens, fullWidth?: boolean): React.CSSProperties {
  return {
    width: fullWidth ? '100%' : 240,
    borderRadius: t.radiusSm,
    background: t.surfaceInset,
    border: `1px solid ${t.border}`,
  };
}

export function primaryButtonStyle(t: ThemeTokens): React.CSSProperties {
  return {
    background: t.primary,
    border: 'none',
    color: '#ffffff',
    fontWeight: 500,
    height: 36,
    borderRadius: t.radiusSm,
  };
}

// ─── Status Colors ──────────────────────────────────────────
export function statusColor(
  t: ThemeTokens,
  status: 'success' | 'danger' | 'warning' | 'info' | 'muted'
): string {
  const map = {
    success: t.success,
    danger: t.danger,
    warning: t.warning,
    info: t.info,
    muted: t.textMuted,
  };
  return map[status];
}

export function statusMutedColor(
  t: ThemeTokens,
  status: 'success' | 'danger' | 'warning' | 'info' | 'muted'
): string {
  const map = {
    success: t.successMuted,
    danger: t.dangerMuted,
    warning: t.warningMuted,
    info: t.infoMuted,
    muted: t.borderSoft,
  };
  return map[status];
}
